import {
  API_ERROR_CODES,
  ValidationErrorClass,
  type ApiErrorCode,
} from '../types/api/errors';
import { AUTH_ERROR_CODES, type AuthErrorCode } from '../types/auth';
import { PASSWORD_MIN_LENGTH } from '../validation/auth.schemas';

/**
 * User-friendly error messages for authentication errors
 */
export const AUTH_ERROR_MESSAGES: Record<AuthErrorCode, string> = {
  // Login errors
  [AUTH_ERROR_CODES.INVALID_CREDENTIALS]:
    'Invalid email or password. Please check your credentials and try again.',
  [AUTH_ERROR_CODES.ACCOUNT_LOCKED]:
    'Your account has been temporarily locked due to multiple failed login attempts. Please try again later or contact support.',
  [AUTH_ERROR_CODES.EMAIL_NOT_VERIFIED]:
    'Please verify your email address before logging in. Check your inbox for a verification email.',
  [AUTH_ERROR_CODES.TOO_MANY_ATTEMPTS]:
    'Too many login attempts. Please wait a few minutes before trying again.',

  // Registration errors
  [AUTH_ERROR_CODES.EMAIL_ALREADY_EXISTS]:
    'An account with this email address already exists. Please use a different email or try logging in.',
  [AUTH_ERROR_CODES.WEAK_PASSWORD]: `Password is too weak. Please choose one with at least ${PASSWORD_MIN_LENGTH} characters, including a letter, that has not appeared in a data leak.`,
  [AUTH_ERROR_CODES.TERMS_NOT_ACCEPTED]:
    'Please accept the terms and conditions to continue.',

  // Token errors
  [AUTH_ERROR_CODES.TOKEN_EXPIRED]:
    'Your session has expired. Please log in again to continue.',
  [AUTH_ERROR_CODES.TOKEN_INVALID]:
    'Invalid authentication token. Please log in again.',
  [AUTH_ERROR_CODES.REFRESH_TOKEN_EXPIRED]:
    'Your session has expired. Please log in again.',
  [AUTH_ERROR_CODES.SESSION_EXPIRED]:
    'Your session has expired. Please log in again.',

  // CSRF errors
  [AUTH_ERROR_CODES.CSRF_TOKEN_MISMATCH]:
    'Security token mismatch. Please refresh the page and try again.',
  [AUTH_ERROR_CODES.CSRF_TOKEN_EXPIRED]:
    'Security token expired. Please refresh the page and try again.',

  // Password reset errors
  [AUTH_ERROR_CODES.RESET_TOKEN_EXPIRED]:
    'Password reset link has expired. Please request a new password reset.',
  [AUTH_ERROR_CODES.RESET_TOKEN_INVALID]:
    'Invalid password reset link. Please request a new password reset.',
  [AUTH_ERROR_CODES.PASSWORD_RESET_FAILED]:
    'Password reset failed. Please try again or contact support.',

  // General errors
  [AUTH_ERROR_CODES.UNAUTHORIZED]:
    'You are not authorized to perform this action. Please log in and try again.',
  [AUTH_ERROR_CODES.FORBIDDEN]:
    'Access denied. You do not have permission to perform this action.',
  [AUTH_ERROR_CODES.NETWORK_ERROR]:
    'Network connection failed. Please check your internet connection and try again.',
  [AUTH_ERROR_CODES.SERVER_ERROR]:
    'Server error occurred. Please try again later or contact support if the problem persists.',
  [AUTH_ERROR_CODES.UNKNOWN_ERROR]:
    'An unexpected error occurred. Please try again or contact support if the problem persists.',
};

/**
 * User-friendly error messages for API errors
 */
export const API_ERROR_MESSAGES: Record<ApiErrorCode, string> = {
  // Authentication errors
  [API_ERROR_CODES.UNAUTHENTICATED]:
    'Your session has expired. Please log in again.',
  [API_ERROR_CODES.TOKEN_EXPIRED]:
    'Your session has expired. Please log in again.',
  [API_ERROR_CODES.TOKEN_INVALID]:
    'Authentication failed. Please log in again.',
  [API_ERROR_CODES.SESSION_ENDED]: 'Your session ended before this finished.',
  [API_ERROR_CODES.TOKEN_MISSING]: 'Authentication required. Please log in.',
  [API_ERROR_CODES.REFRESH_TOKEN_EXPIRED]:
    'Session expired. Please log in again.',
  [API_ERROR_CODES.REFRESH_TOKEN_INVALID]:
    'Authentication failed. Please log in again.',
  [API_ERROR_CODES.INVALID_CREDENTIALS]:
    'Invalid email or password. Please try again.',
  [API_ERROR_CODES.ACCOUNT_LOCKED]:
    'Your account has been locked. Please contact support.',
  [API_ERROR_CODES.EMAIL_NOT_VERIFIED]:
    'Please verify your email address before continuing.',
  [API_ERROR_CODES.TWO_FACTOR_REQUIRED]: 'Two-factor authentication required.',

  // Authorization errors
  [API_ERROR_CODES.FORBIDDEN]:
    'Access denied. You do not have permission to perform this action.',
  [API_ERROR_CODES.INSUFFICIENT_PERMISSIONS]:
    'Insufficient permissions. Please contact your administrator.',
  [API_ERROR_CODES.RESOURCE_FORBIDDEN]: 'Access to this resource is forbidden.',
  [API_ERROR_CODES.PAYMENT_REQUIRED]:
    'A subscription is required to access this feature.',
  [API_ERROR_CODES.CONFIRMATION_REQUIRED]: "Confirm it's you to continue.",
  [API_ERROR_CODES.READ_ONLY]:
    "Read-only: you can't make changes while signed in from the admin panel.",

  // Validation errors
  [API_ERROR_CODES.VALIDATION_ERROR]: 'Please check your input and try again.',
  [API_ERROR_CODES.INVALID_INPUT]:
    'Invalid input provided. Please check your data and try again.',
  [API_ERROR_CODES.MISSING_REQUIRED_FIELD]:
    'Required fields are missing. Please complete all required fields.',
  [API_ERROR_CODES.INVALID_FORMAT]: 'Invalid format. Please check your input.',
  [API_ERROR_CODES.VALUE_TOO_LONG]:
    'Value is too long. Please shorten your input.',
  [API_ERROR_CODES.VALUE_TOO_SHORT]:
    'Value is too short. Please provide more information.',
  [API_ERROR_CODES.INVALID_EMAIL]: 'Please enter a valid email address.',
  [API_ERROR_CODES.WEAK_PASSWORD]:
    'Password is too weak. Please use a stronger password.',
  [API_ERROR_CODES.PASSWORD_MISMATCH]:
    'Passwords do not match. Please try again.',

  // Resource errors
  [API_ERROR_CODES.NOT_FOUND]: 'The requested resource was not found.',
  [API_ERROR_CODES.RESOURCE_NOT_FOUND]: 'The requested resource was not found.',
  [API_ERROR_CODES.RESOURCE_ALREADY_EXISTS]: 'This resource already exists.',
  [API_ERROR_CODES.RESOURCE_CONFLICT]:
    'Resource conflict occurred. Please try again.',
  [API_ERROR_CODES.RESOURCE_STALE]: 'This has changed since you last read it.',
  [API_ERROR_CODES.RESOURCE_DELETED]: 'This resource has been deleted.',

  // Server errors
  [API_ERROR_CODES.SERVER_ERROR]:
    'Internal server error. Please try again later.',
  [API_ERROR_CODES.SERVICE_UNAVAILABLE]:
    'Service is temporarily unavailable. Please try again later.',
  [API_ERROR_CODES.BAD_GATEWAY]: 'Bad gateway error. Please try again later.',
  [API_ERROR_CODES.GATEWAY_TIMEOUT]: 'Request timeout. Please try again.',
  [API_ERROR_CODES.DATABASE_ERROR]:
    'Database error occurred. Please try again later.',
  [API_ERROR_CODES.THIRD_PARTY_SERVICE_ERROR]:
    'External service error. Please try again later.',

  // Network errors
  [API_ERROR_CODES.NETWORK_ERROR]:
    'Network connection failed. Please check your internet connection.',
  [API_ERROR_CODES.TIMEOUT_ERROR]: 'Request timeout. Please try again.',
  [API_ERROR_CODES.CONNECTION_ERROR]:
    'Connection failed. Please check your internet connection.',
  [API_ERROR_CODES.DNS_ERROR]:
    'DNS resolution failed. Please check your internet connection.',
  [API_ERROR_CODES.SSL_ERROR]:
    'SSL connection error. Please check your internet connection.',

  // Rate limiting
  [API_ERROR_CODES.RATE_LIMITED]:
    'Too many requests. Please wait a moment before trying again.',
  [API_ERROR_CODES.TOO_MANY_REQUESTS]:
    'Too many requests. Please slow down and try again.',
  [API_ERROR_CODES.QUOTA_EXCEEDED]: 'Quota exceeded. Please try again later.',

  // Maintenance
  [API_ERROR_CODES.MAINTENANCE_MODE]:
    'Service is under maintenance. Please try again later.',
  [API_ERROR_CODES.SERVICE_DEGRADED]:
    'Service is experiencing issues. Some features may be unavailable.',

  // Client errors
  [API_ERROR_CODES.BAD_REQUEST]:
    'Invalid request. Please check your input and try again.',
  [API_ERROR_CODES.INVALID_JSON]:
    'Invalid data format. Please check your input.',
  [API_ERROR_CODES.UNSUPPORTED_MEDIA_TYPE]:
    'Unsupported file type. Please use a different format.',
  [API_ERROR_CODES.PAYLOAD_TOO_LARGE]:
    'Request is too large. Please reduce the size and try again.',

  // Unknown
  [API_ERROR_CODES.UNKNOWN_ERROR]:
    'An unexpected error occurred. Please try again later.',
  [API_ERROR_CODES.UNEXPECTED_ERROR]:
    'An unexpected error occurred. Please try again later.',
};

/**
 * Success messages for authentication actions
 */
export const AUTH_SUCCESS_MESSAGES = {
  LOGIN_SUCCESS: 'Welcome back! You have been successfully logged in.',
  LOGOUT_SUCCESS: 'You have been successfully logged out.',
  REGISTER_SUCCESS:
    'Account created successfully! Please check your email to verify your account.',
  PASSWORD_RESET_REQUEST_SUCCESS:
    'Password reset instructions have been sent to your email.',
  PASSWORD_RESET_SUCCESS:
    'Your password has been successfully reset. You can now log in with your new password.',
  EMAIL_VERIFICATION_SENT:
    'Verification email has been sent. Please check your inbox.',
  EMAIL_VERIFIED: 'Your email has been successfully verified.',
  PROFILE_UPDATED: 'Your profile has been successfully updated.',
  PASSWORD_CHANGED: 'Your password has been successfully changed.',
} as const;

/**
 * Loading messages for authentication actions
 */
export const AUTH_LOADING_MESSAGES = {
  LOGGING_IN: 'Signing you in...',
  LOGGING_OUT: 'Signing you out...',
  REGISTERING: 'Creating your account...',
  RESETTING_PASSWORD: 'Resetting your password...',
  SENDING_EMAIL: 'Sending email...',
  VERIFYING_EMAIL: 'Verifying your email...',
  UPDATING_PROFILE: 'Updating your profile...',
  CHANGING_PASSWORD: 'Changing your password...',
} as const;

/**
 * Get user-friendly error message for authentication error
 */
export function getAuthErrorMessage(errorCode: AuthErrorCode): string {
  return (
    AUTH_ERROR_MESSAGES[errorCode] ||
    AUTH_ERROR_MESSAGES[AUTH_ERROR_CODES.UNKNOWN_ERROR]
  );
}

/**
 * Get user-friendly error message for API error
 */
export function getApiErrorMessage(errorCode: ApiErrorCode): string {
  return (
    API_ERROR_MESSAGES[errorCode] ||
    API_ERROR_MESSAGES[API_ERROR_CODES.SERVER_ERROR]
  );
}

/**
 * Get success message for authentication action
 */
export function getAuthSuccessMessage(
  action: keyof typeof AUTH_SUCCESS_MESSAGES
): string {
  return AUTH_SUCCESS_MESSAGES[action];
}

/**
 * Get loading message for authentication action
 */
export function getAuthLoadingMessage(
  action: keyof typeof AUTH_LOADING_MESSAGES
): string {
  return AUTH_LOADING_MESSAGES[action];
}

/**
 * Error message variants for different UI contexts
 */
export const ERROR_MESSAGE_VARIANTS = {
  FORM: 'form',
  TOAST: 'toast',
  MODAL: 'modal',
  INLINE: 'inline',
} as const;

export type ErrorMessageVariant =
  (typeof ERROR_MESSAGE_VARIANTS)[keyof typeof ERROR_MESSAGE_VARIANTS];

/**
 * Get contextual error message based on variant
 */
export function getContextualErrorMessage(
  errorCode: AuthErrorCode | ApiErrorCode,
  variant: ErrorMessageVariant = ERROR_MESSAGE_VARIANTS.FORM
): string {
  const baseMessage = Object.values(AUTH_ERROR_CODES).includes(
    errorCode as AuthErrorCode
  )
    ? getAuthErrorMessage(errorCode as AuthErrorCode)
    : getApiErrorMessage(errorCode as ApiErrorCode);

  switch (variant) {
    case ERROR_MESSAGE_VARIANTS.TOAST:
      return baseMessage;
    case ERROR_MESSAGE_VARIANTS.MODAL:
      return `${baseMessage} If this problem persists, please contact support.`;
    case ERROR_MESSAGE_VARIANTS.INLINE:
      return baseMessage;
    case ERROR_MESSAGE_VARIANTS.FORM:
    default:
      return baseMessage;
  }
}

/**
 * The message an error carries, to show the person it happened to, or
 * `fallback` when it carries none. Anything with a non-empty string `message`
 * counts: an `Error`, an `ApiErrorClass`, the auth service's plain `AuthError`
 * objects.
 *
 * One helper for web and mobile, which had each kept their own copy. Mobile
 * imports it by subpath (`shared/src/utils/error-messages`), never through the
 * root barrel, which isn't React Native-safe.
 */
export function getErrorMessage(error: unknown, fallback: string): string {
  if (error && typeof error === 'object' && 'message' in error) {
    const { message } = error as { message?: unknown };
    if (typeof message === 'string' && message.length > 0) return message;
  }
  return fallback;
}

/**
 * The first validation message an error carries for `field`, or undefined
 * when it has none, across the shapes the services throw:
 *  - an `apiClient` 422: `ValidationErrorClass`, its messages keyed by field
 *  - an auth-client 422: `AuthError.details` = `{ [field]: [messages] }`
 *  - a client-side zod failure: `InvalidDataError.validationErrors` =
 *    `[{ field, message }]`
 *
 * Undefined lets the caller fall back, to a toast say. One helper for web and
 * mobile, which had each kept their own copy; mobile imports it by subpath.
 */
export function getFieldErrorMessage(
  error: unknown,
  field: string
): string | undefined {
  if (!error || typeof error !== 'object') return undefined;

  if (error instanceof ValidationErrorClass) {
    return error.getFieldErrors(field)[0];
  }

  const details = (error as { details?: unknown }).details;
  if (details && typeof details === 'object') {
    const messages = (details as Record<string, unknown>)[field];
    if (Array.isArray(messages) && typeof messages[0] === 'string') {
      return messages[0];
    }
  }

  const validationErrors = (error as { validationErrors?: unknown })
    .validationErrors;
  if (Array.isArray(validationErrors)) {
    for (const entry of validationErrors) {
      if (
        entry &&
        typeof entry === 'object' &&
        (entry as { field?: unknown }).field === field &&
        typeof (entry as { message?: unknown }).message === 'string'
      ) {
        return (entry as { message: string }).message;
      }
    }
  }

  return undefined;
}

/** Per-field validation messages for the password-change form. */
export interface PasswordFieldErrors {
  current?: string;
  new?: string;
  confirm?: string;
}

/**
 * The server's Laravel validation keys, each onto the password-change form's
 * field it belongs to, so a wrong `current_password` lands on its input.
 */
const PASSWORD_FIELD_MAP: Record<string, keyof PasswordFieldErrors> = {
  current_password: 'current',
  new_password: 'new',
  new_password_confirmation: 'confirm',
};

/**
 * A refused password change's validation messages, placed on the form's
 * fields. Empty when the error carries none, so the caller shows a toast
 * instead. One helper for web and mobile; mobile imports it by subpath.
 */
export function mapPasswordServerErrors(error: unknown): PasswordFieldErrors {
  const mapped: PasswordFieldErrors = {};
  for (const [serverField, field] of Object.entries(PASSWORD_FIELD_MAP)) {
    const message = getFieldErrorMessage(error, serverField);
    if (message) mapped[field] = message;
  }
  return mapped;
}
